// Admin > Users tab — searchable table, CSV export, per-row delete.
// Presentational; parent owns fetching + delete logic (passed as props).
import { Search, Download, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmt, csvDownload } from "./_helpers";

export default function UsersTab({
  users, usersQ, setUsersQ, loadUsers, loading, deleteUser, currentUserEmail,
}) {
  return (
    <div className="space-y-4" data-testid="admin-users">
      <form
        onSubmit={(e) => { e.preventDefault(); loadUsers(usersQ); }}
        className="flex gap-2 flex-wrap"
      >
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={usersQ}
            onChange={(e) => setUsersQ(e.target.value)}
            placeholder="Search email or name"
            className="pl-9"
            data-testid="admin-users-search"
          />
        </div>
        <Button type="submit" variant="outline" data-testid="admin-users-search-button">Search</Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => csvDownload(
            "users.csv",
            users,
            [
              { label: "Email", get: (r) => r.email },
              { label: "Name", get: (r) => r.name },
              { label: "Dogs", get: (r) => r.dog_count },
              { label: "Subscription", get: (r) => r.subscription?.status || "none" },
              { label: "Plan", get: (r) => r.subscription?.plan || "" },
              { label: "Created", get: (r) => r.created_at || "" },
            ],
          )}
          data-testid="admin-users-export"
        >
          <Download size={14} className="mr-1.5" /> Export CSV
        </Button>
      </form>

      <Card className="border-border shadow-none rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3 text-right">Dogs</th>
                <th className="px-4 py-3">Subscription</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 text-right w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">{loading ? "Loading…" : "No users"}</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.email}</td>
                  <td className="px-4 py-3">{u.name}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{u.dog_count}</td>
                  <td className="px-4 py-3">
                    {u.subscription?.status ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs font-medium">
                        {u.subscription.plan || "Pro"} · {u.subscription.status}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Free</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{u.created_at ? fmt(u.created_at) : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => deleteUser(u)}
                      disabled={u.email === currentUserEmail}
                      data-testid={`admin-user-delete-${u.id}`}
                      className="p-1.5 rounded text-muted-foreground hover:bg-destructive hover:text-destructive-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title={u.email === currentUserEmail ? "Can't delete your own admin account" : "Delete user and all their data"}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
